import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  DownloadOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { Col, Input, Modal, Row, Select, Spin, Table } from "antd";
import { useFormik } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import ExpenseAccountType from "../../services/api/ExpenseAccountType";
import { paginationDefaults } from "../../utils/properties";
import { buildQueryString, exportFile, showError, showSuccess, trimData } from "../../utils/utilities";
import Filter from "../../components/functional_components/Filter/Filter";
import { setFilters } from "../../redux/ducks/Filters";

const ExpenseAccountTypes = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { confirm } = Modal;
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const formikExpenseAccountType = useFormik({
    initialValues: {
      name: get(state.modal.data, "name", ""),
      description: get(state.modal.data, "description", ""),
      code: get(state.modal.data, "code", ""),
      category: get(state.modal.data, "category", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.name) {
        errors.name = "Required!";
      }
      if (!values.description) {
        errors.description = "Required!";
      }
      if (!values.code) {
        errors.code = "Required!";
      }
      if (!values.category) {
        errors.category = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        id: state.modal.data?.id || undefined,
      };
      const saveResponse = await ExpenseAccountType.setExpenseAccountType(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } =
      await ExpenseAccountType.getExpenseAccountTypes(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikExpenseAccountType.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await ExpenseAccountType.getExpenseAccountType(id);
    if (!error && data) {
      setState((prevState) => ({ ...prevState, modal: { visible: true, type: "edit", data } }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await ExpenseAccountType.deleteExpenseAccountType(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("expenseAccountType.table.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("expenseAccountType.table.description"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: t("expenseAccountType.table.code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("expenseAccountType.table.category"),
      dataIndex: "category",
      key: "category",
      render: (category) => t(`expenseAccountType.${category}`),
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const formikFilter = useFormik({
    initialValues: {
      name: "",
      description: "",
      code: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      values["page"] = 0;
      const queryString = buildQueryString(filters.obj, values);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "name",
      name: "name",
      value: formikFilter.values.name,
      label: t("expenseAccountType.table.name"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "description",
      name: "description",
      value: formikFilter.values.description,
      label: t("expenseAccountType.table.description"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "code",
      name: "code",
      value: formikFilter.values.code,
      label: t("expenseAccountType.table.code"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const downloadExport = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await ExpenseAccountType.downloadExportFile();
    if (!exportResponse.error) {
      const blob = new Blob([exportResponse]);
      exportFile(blob, "Expense_Account_Type.csv");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
        <Button
          className="ml-10"
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={() => downloadExport()}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("expenseAccountType.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikExpenseAccountType.submitForm}
            isDisabled={!(formikExpenseAccountType.dirty && formikExpenseAccountType.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="name">
                {t("expenseAccountType.table.name")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="name"
                name="name"
                onChange={formikExpenseAccountType.handleChange}
                onBlur={formikExpenseAccountType.handleBlur}
                value={formikExpenseAccountType.values.name}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="description">
                {t("expenseAccountType.table.description")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="description"
                name="description"
                onChange={formikExpenseAccountType.handleChange}
                onBlur={formikExpenseAccountType.handleBlur}
                value={formikExpenseAccountType.values.description}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]}>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="code">
                {t("expenseAccountType.table.code")}*
              </label>
            </Row>
            <Row>
              <Input
                required
                id="code"
                name="code"
                onChange={formikExpenseAccountType.handleChange}
                onBlur={formikExpenseAccountType.handleBlur}
                value={formikExpenseAccountType.values.code}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="category">
                {t("expenseAccountType.table.category")}*
              </label>
            </Row>
            <Select
              className="w-100"
              id="category"
              optionFilterProp="value"
              name="category"
              placeholder={t("expenseAccountType.table.category")}
              onChange={(value) => {
                formikExpenseAccountType.setFieldValue("category", value);
              }}
              onBlur={() => {
                formikExpenseAccountType.handleBlur("category");
              }}
              value={formikExpenseAccountType.values.category}
              options={[
                { label: t("expenseAccountType.INVOICE"), value: "INVOICE" },
                { label: t("expenseAccountType.MEAL"), value: "MEAL" },
                { label: t("expenseAccountType.FUEL"), value: "FUEL" },
                { label: t("expenseAccountType.OTHER"), value: "OTHER" },
              ]}
            />
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default ExpenseAccountTypes;
